import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
  IPublicClientApplication,
  InteractionType,
  PublicClientApplication,
  BrowserCacheLocation,
  LogLevel
} from '@azure/msal-browser';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG
} from '@azure/msal-angular';
import { environment } from '../environments/environment';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatIconModule} from '@angular/material/icon';
import { LayoutModule } from '@angular/cdk/layout';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { FaturasComponent } from './faturas/faturas.component';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatCardModule } from '@angular/material/card';
import {MatTabsModule} from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { OverlayModule } from '@angular/cdk/overlay'
import { ReactiveFormsModule } from '@angular/forms';
import { BuscarFaturasComponent } from './faturas/buscar-faturas/buscar-faturas.component';
import {MatNativeDateModule, MatRippleModule, MAT_DATE_LOCALE} from '@angular/material/core';
import { GerarFaturasComponent } from './faturas/gerar-faturas/gerar-faturas.component';
import { ListaFaturasComponent } from './faturas/lista-faturas/lista-faturas.component';
import { ConfirmDialogComponent } from './shared/confirm-dialog/confirm-dialog.component';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.entraAuth.clientId,
      authority: `https://login.microsoftonline.com/${environment.entraAuth.tenantId}`,
      // Página estática (sem Angular) dedicada a receber a resposta do login - evita que o
      // Router (hash routing, #/...) reescreva o hash antes do MSAL conseguir ler a resposta.
      // Precisa estar cadastrada como Redirect URI (tipo SPA) no app registration do Entra ID.
      redirectUri: window.location.origin + '/auth-popup.html'
    },
    cache: {
      // sessão não sobrevive ao fechar a aba/navegador, por decisão de segurança.
      cacheLocation: BrowserCacheLocation.SessionStorage
    },
    system: {
      // TEMPORÁRIO pra depuração do 401 - reverter depois de confirmar a causa.
      loggerOptions: {
        logLevel: LogLevel.Verbose,
        loggerCallback: (level, message) => console.log('[MSAL]', message)
      }
    }
  });
}

// Mesmo padrão do Gestao MC: token de acesso para o escopo customizado exposto
// no App Registration (Expose an API), não ID token - o backend valida essa audience.
const apiScopes = [`api://${environment.entraAuth.clientId}/access_as_user`];

// Popup em vez de redirect: o app usa hash routing (#/...) e a resposta de login por
// redirect (?code=...) colide com o hash do router. Popup evita esse conflito de vez.
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Popup,
    authRequest: { scopes: apiScopes }
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  // Coringa (*) em vez de URLs exatas: MsalInterceptor normaliza a URL via Location
  // (hash routing) antes de comparar, o que quebra o match exato com querystring.
  protectedResourceMap.set(window.location.origin + '/Faturamento/*', apiScopes);

  return { interactionType: InteractionType.Popup, protectedResourceMap };
}

@NgModule({
  declarations: [
    AppComponent,
    FaturasComponent,
    BuscarFaturasComponent,
    GerarFaturasComponent,
    ListaFaturasComponent,
    ConfirmDialogComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    MatToolbarModule,
    MatIconModule,
    LayoutModule,
    MatButtonModule,
    MatSidenavModule,
    MatListModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatCardModule,
    MatTabsModule,
    MatTableModule,
    MatChipsModule,
    MatDialogModule,
    MatSnackBarModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatRippleModule,
    MatProgressSpinnerModule,
    OverlayModule,
    ReactiveFormsModule,
    MsalModule
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
